import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dig from 'object-dig';
import moment from 'moment';

import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import CloseIcon from '@material-ui/icons/Close';
import RemoveIcon from '@material-ui/icons/Remove';
import CheckIcon from '@material-ui/icons/Check';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import Modal from '@material-ui/core/Modal';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

import Button from '~/components/Button';
import Paper from '~/components/Paper';
import LinearProgress from '~/components/LinearProgress';
import GridContainer from '~/components/GridContainer';
import IconButton from '~/components/Button/IconButton';
import GridItem from '~/components/GridItem';
import ModalAction from '~/components/Modal/ModalAction';
import Table from '~/components/Table/Table';
import tableStyles from '~/components/Table/Table/styles';
import InputMonthPicker from '~/components/Input/InputMonthPicker';

import Request from '~/providers/Request';
import formatNumber from '~/utils/Number/formatNumber';

import useStyles from './styles';

function ModalFolder(props) {
  const { open, onClose, id } = props;
  const { t } = useTranslation();
  const classesTable = tableStyles();
  const classes = useStyles();

  const [dataInit, setDataInit] = useState(moment().startOf('month').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('month').format());

  const [loading, setLoading] = useState(false);
  const [loadingSyncQuestor, setLoadingSyncQuestor] = useState(false);
  const [isModalPyasments, setIsModalPyasments] = useState(false);

  const [payments, setPayments] = useState([]);

  async function syncQuestor() {
    setLoading(true);
    setLoadingSyncQuestor(true);

    const data = payments.map((row) => {
      return {
        corporation_payment_id: row.corporationPayment.id,
        count: row.count,
        total_price: row.total_price,
        unitary_price: row.unitary_price,
        competency_data: dataInit,
      };
    });

    Request.post(
      `/admin/corporation/payment_services/${id}/sync_questor`,
      data,
      {},
      { controller: 'corporationPaymentServices' }
    )

      .then(() => {
        setLoading(false);
        setLoadingSyncQuestor(false);
        setIsModalPyasments(false);
        onClose();
        toast.success(t('corporation.extras.syncQuestorSuccess'));
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
        setLoadingSyncQuestor(false);
        toast.error(t('corporation.extras.syncQuestorFail'));
      });
  }

  async function getPayments() {
    setLoading(true);

    Request.get(
      `/admin/corporation/payment_services/${id}/calculate_payments`,
      { start: dataInit, end: dataEnd },
      { normalizeJSON: false }
    )

      .then((response) => {
        setPayments(response);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
      });
  }

  function Row(props) {
    const { row } = props;
    const [open, setOpen] = useState(false);

    return (
      <>
        <TableRow hover className={classesTable.row}>
          <TableCell>{dig(row, 'paymentService', 'name')}</TableCell>
          <TableCell>{formatNumber(dig(row, 'unitary_price'))}</TableCell>
          <TableCell>{dig(row, 'count')}</TableCell>
          <TableCell>{formatNumber(dig(row, 'total_price'))}</TableCell>
          <TableCell align="right">
            <IconButton
              tooltip={t('action.show')}
              onClick={() => {
                setOpen(!open);
              }}
              Icon={open ? ExpandLessIcon : ExpandMoreIcon}
              iconColor="primary"
            />
          </TableCell>
        </TableRow>

        {open && (
          <>
            <TableRow>
              <TableCell>
                {t('corporationPaymentServices.attributes.competency_data')}
              </TableCell>
              <TableCell align="right">
                {moment(dataInit).format('MM/YYYY')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t('corporationPaymentServices.attributes.price')}
              </TableCell>
              <TableCell align="right">
                {formatNumber(dig(row, 'corporationPayment', 'price'))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t('corporationPaymentServices.attributes.is_fixed')}
              </TableCell>
              <TableCell align="right">
                {row.corporationPayment.is_fixed ? (
                  <CheckIcon />
                ) : (
                  <CloseIcon />
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t('corporationPaymentServices.attributes.fixed_amount')}
              </TableCell>
              <TableCell align="right">
                {row.corporationPayment.is_fixed ? (
                  row.corporationPayment.fixed_amount
                ) : (
                  <RemoveIcon />
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t('corporationPaymentServices.attributes.excess_price')}
              </TableCell>
              <TableCell align="right">
                {row.corporationPayment.is_fixed ? (
                  formatNumber(row.corporationPayment.excess_price)
                ) : (
                  <RemoveIcon />
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t('corporationPaymentServices.attributes.payment_wallet')}
              </TableCell>
              <TableCell align="right">
                {dig(row, 'paymentWallet', 'name')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t('corporationPaymentServices.attributes.payment_method_id')}
              </TableCell>
              <TableCell align="right">
                {dig(row, 'paymentMethod', 'name')}
              </TableCell>
            </TableRow>
          </>
        )}
      </>
    );
  }

  useEffect(() => {
    if (id && open) {
      getPayments();
    }
  }, [dataInit, dataEnd, id, open]);

  return (
    <Modal open={open} onClose={onClose}>
      <div className={classes.root}>
        <Paper
          classes={{
            root: classes.paper,
          }}
          Icon={MonetizationOnIcon}
          iconColor="blue"
          title={t('corporation.tabs.financial')}
          actions={
            <IconButton
              tooltip={t('action.close')}
              onClick={onClose}
              Icon={CloseIcon}
            />
          }
        >
          <GridContainer>
            <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
              <InputMonthPicker
                setStartDate={(date) => setDataInit(date)}
                setEndDate={(date) => setDataEnd(date)}
              />
            </GridItem>
          </GridContainer>

          <LinearProgress loading={loading} />

          <GridContainer>
            <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
              <Table
                emptyData={payments.length === 0}
                loading={loading}
                columns={
                  <>
                    <TableCell className={classesTable.cell}>
                      {t('corporationPaymentServices.attributes.service')}
                    </TableCell>
                    <TableCell className={classesTable.cell}>
                      {t('corporationPaymentServices.attributes.unitary_price')}
                    </TableCell>
                    <TableCell className={classesTable.cell}>
                      {t('corporationPaymentServices.attributes.count')}
                    </TableCell>
                    <TableCell className={classesTable.cell}>
                      {t('corporationPaymentServices.attributes.total_price')}
                    </TableCell>
                    <TableCell className={classesTable.cell}></TableCell>
                  </>
                }
              >
                {payments.map((row, index) => {
                  return <Row key={index} row={row} />;
                })}
              </Table>
            </GridItem>
          </GridContainer>

          <LinearProgress loading={loading} />

          <ModalAction
            open={isModalPyasments}
            loading={loadingSyncQuestor}
            Icon={MonetizationOnIcon}
            iconColor={'purple'}
            text={t('corporation.extras.syncQuestorText')}
            buttonColor={'green'}
            buttonText={t('corporation.extras.savePaymets')}
            onAction={syncQuestor}
            onClose={() => {
              setIsModalPyasments(false);
            }}
          />

          <div
            style={{ marginTop: 40, textAlign: 'right', borderColor: 'black' }}
          >
            <hr />
            <Button
              variant="contained"
              color="grey"
              onClick={onClose}
              className={classes.button}
            >
              {t('form-cancel')}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => setIsModalPyasments(true)}
              className={classes.button}
            >
              {t('corporation.extras.savePaymets')}
            </Button>
          </div>
        </Paper>
      </div>
    </Modal>
  );
}

ModalFolder.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};
export default ModalFolder;
